import { useState, useCallback } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { cn, initials, avatarColor } from "@/lib/utils";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { sanitizeMessageHtml } from "@/lib/sanitizeMessageHtml";
import type { JobMessage } from "@/data/jobMessageTypes";
import {
  CheckCircle,
  Circle,
  Clock,
  X,
} from "lucide-react";
import { JobMessageService } from "@/services/job-message.service";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

interface JobChatMessageRendererProps {
  message: JobMessage;
  isOwnMessage: boolean;
  showAvatar: boolean;
  showTail: boolean;
  compact?: boolean;
}

const CATEGORY_COLORS: Record<string, string> = {
  orders: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  concerns: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  general: "bg-slate-500/10 text-slate-600 border-slate-500/20",
};

const PRIORITY_BORDER = {
  urgent: "border-l-2 border-red-500",
  high: "border-l-2 border-amber-500",
  normal: "",
};

export function JobChatMessageRenderer({
  message,
  isOwnMessage,
  showAvatar,
  showTail,
  compact = false,
}: JobChatMessageRendererProps) {
  const { profile } = useCurrentUser();
  const categoryColor = CATEGORY_COLORS[message.category] ?? CATEGORY_COLORS.general;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState("");
  const [lightboxCaption, setLightboxCaption] = useState("");

  const readStatus = message.recipientStatuses?.[profile?.id ?? ""] ?? "unread";
  const isRead = readStatus !== "unread" || isOwnMessage;

  const isAuthorizedDeleter =
    isOwnMessage ||
    profile?.role === "ops_manager" ||
    profile?.role === "admin" ||
    profile?.role === "warehouse_manager";

   const handleDeleteInvoice = useCallback(
    async (attachmentId?: string) => {
      const id = attachmentId;
      const currentBody = message.body;

      if (id) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = currentBody;
        const figureEl = tempDiv.querySelector(`figure[data-attachment-id="${id}"]`);
        if (figureEl) {
          figureEl.remove();
        }
        const newBody = tempDiv.innerHTML;
        const finalBody = newBody.trim()
          ? newBody
          : `<p class="text-muted-foreground italic text-xs">[Invoice image removed]</p>`;

        try {
          await JobMessageService.updateBody(message.id, finalBody);
          const { data: attachment } = await supabase
            .from("attachments")
            .select("id")
            .eq("id", id)
            .single();
          if (attachment) {
            const { deleteAttachment } = await import("@/services/attachment.service");
            void deleteAttachment(id);
          }
          toast.success("Invoice image deleted");
        } catch {
          toast.error("Failed to delete invoice image");
        }
      }
    },
    [message.id, message.body],
  );

  const handleDeleteInvoiceWithId = useCallback(
    (attachmentId: string | null) => {
      if (attachmentId) {
        void handleDeleteInvoice(attachmentId);
      }
    },
    [handleDeleteInvoice],
  );

const handleInvoiceClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const deleteBtn = target.closest("button.chat-invoice-delete");
    if (deleteBtn) {
      e.stopPropagation();
      if (!isAuthorizedDeleter) return;
      const figure = deleteBtn.closest("figure.chat-invoice-figure");
      if (figure) {
        const attachmentId = figure.getAttribute("data-attachment-id");
        handleDeleteInvoiceWithId(attachmentId);
      }
      return;
    }
    const img = target.closest("img");
    if (img && img.closest("figure.chat-invoice-figure")) {
      const src = img.getAttribute("src");
      if (src) {
        setLightboxUrl(src);
        const figure = img.closest("figure.chat-invoice-figure");
        const figcaption = figure?.querySelector("figcaption.chat-invoice-caption");
        setLightboxCaption(figcaption?.textContent ?? "");
        setLightboxOpen(true);
      }
    }
  }, [handleDeleteInvoiceWithId]);

  const handleLightboxClose = useCallback(() => {
    setLightboxOpen(false);
    setLightboxUrl("");
    setLightboxCaption("");
  }, []);

  return (
    <div
      className={cn(
        "group relative",
        isOwnMessage ? "flex justify-end" : "flex justify-start",
      )}
    >
      {showAvatar && !isOwnMessage && (
        <Avatar className="h-7 w-7 mr-2 mt-0.5 shrink-0">
          <AvatarImage src="" alt={message.senderName} />
          <AvatarFallback
            className="text-xs font-medium"
            style={{ backgroundColor: avatarColor(message.senderId) }}
          >
            {initials(message.senderName || "U")}
          </AvatarFallback>
        </Avatar>
      )}

        <div
          className={cn(
            "max-w-[70%] rounded-lg px-3 py-2 text-sm break-words relative",
            isOwnMessage
              ? "bg-primary text-primary-foreground rounded-tr-none"
              : "bg-muted/50 rounded-tl-none",
            PRIORITY_BORDER[message.priority ?? "normal"],
            compact ? "text-xs py-1" : "",
          )}
          onClick={handleInvoiceClick}
        >
          {!isOwnMessage && !compact && (
            <div className="font-medium text-xs mb-1 opacity-80">
              {message.senderName}
              {message.senderRole && (
                <span className="ml-1 opacity-60">· {message.senderRole}</span>
              )}
            </div>
          )}

          <div
            className="whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(message.body) }}
          />

          <div
            className={cn(
              "mt-1.5 flex items-center justify-end gap-1 text-[11px]",
              isOwnMessage ? "text-primary-foreground/70" : "text-muted-foreground/70",
            )}
          >
            <span>{format(new Date(message.timestamp), "h:mm a")}</span>
            {isOwnMessage ? (
              readStatus === "responded" ? (
                <CheckCircle size={12} />
              ) : isRead ? (
                <CheckCircle size={12} />
              ) : (
                <Clock size={12} />
              )
            ) : readStatus === "read" ? (
              <CheckCircle size={12} />
            ) : (
              <Circle size={12} />
            )}
          </div>
        </div>

      {!showAvatar && !isOwnMessage && <div className="w-7 mr-2 shrink-0" />}

      {!isOwnMessage && (
        <Badge
          variant="outline"
          className={cn(
            "absolute -bottom-3 left-10 text-[9px] px-1 py-0 h-auto",
            categoryColor,
          )}
        >
          {message.category}
        </Badge>
      )}
      {isOwnMessage && (
        <Badge
          variant="outline"
          className={cn(
            "absolute -bottom-3 right-10 text-[9px] px-1 py-0 h-auto",
            categoryColor,
          )}
        >
          {message.category}
        </Badge>
      )}

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90"
          onClick={handleLightboxClose}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <button
              type="button"
              className="absolute top-2 right-2 z-10 h-8 w-8 flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 transition-colors"
              onClick={handleLightboxClose}
              aria-label="Close lightbox"
            >
              <X className="h-4 w-4 text-white" />
            </button>
            <div className="relative">
              <img
                src={lightboxUrl}
                alt="Invoice preview"
                className="max-h-[85vh] max-w-[85vw] object-contain"
              />
              {lightboxCaption && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-3 py-2 text-xs text-white text-center">
                  {lightboxCaption}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
